import { createStitches, createTheme } from '@stitches/core';
import merge from 'just-merge';
import { SocialLayouts, VIEWS, en, } from '@supabase/auth-ui-shared';
import { EmailAuth, ForgottenPassword, MagicLink, SocialAuth, UpdatePassword, VerifyOtp, } from './interfaces';
import { UserContextProvider, useUser } from './UserContext';
import { createEffect, createMemo, createSignal, Match, mergeProps, onCleanup, onMount, Show, Switch, } from 'solid-js';
import { createStore } from 'solid-js/store';
function Auth(props) {
    /**
     * Localization support
     */
    var mergedProps = createMemo(function () {
        var merged = mergeProps({
            socialLayout: SocialLayouts.horizontal,
            view: 'sign_in',
            onlyThirdPartyProviders: false,
            magicLink: false,
            showLinks: true,
            theme: 'default',
            localization: { variables: {} },
            otpType: 'email'
        }, props);
        return merged;
    });
    var i18n = createMemo(function () {
        var _a;
        var merged = merge(
        //@ts-ignore
        en, (_a = mergedProps().localization.variables) !== null && _a !== void 0 ? _a : {});
        return merged;
    });
    var _a = createSignal(mergedProps().view), authView = _a[0], setAuthView = _a[1];
    var _b = createSignal(''), defaultEmail = _b[0], setDefaultEmail = _b[1];
    var _c = createSignal(''), defaultPassword = _c[0], setDefaultPassword = _c[1];
    var _d = createSignal({}), themes = _d[0], setThemes = _d[1];
    /**
     * Simple boolean to detect if authView 'sign_in' or 'sign_up' is used
     *
     * @returns boolean
     */
    var _e = createSignal(), SignView = _e[0], setSignView = _e[1];
    onMount(function () {
        setSignView(authView() === 'sign_in' ||
            authView() === 'sign_up' ||
            authView() === 'magic_link');
    });
    createEffect(function () {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
        /**
         * Create default theme
         *
         * createStitches()
         * https://stitches.dev/docs/api#theme
         *
         * to add a new theme use  createTheme({})
         * https://stitches.dev/docs/api#theme
         */
        setSignView(authView() === 'sign_in' ||
            authView() === 'sign_up' ||
            authView() === 'magic_link');
        createStitches({
            theme: merge((_c = (_b = (_a = mergedProps().appearance) === null || _a === void 0 ? void 0 : _a.theme) === null || _b === void 0 ? void 0 : _b["default"]) !== null && _c !== void 0 ? _c : {}, (_f = (_e = (_d = mergedProps().appearance) === null || _d === void 0 ? void 0 : _d.variables) === null || _e === void 0 ? void 0 : _e["default"]) !== null && _f !== void 0 ? _f : {})
        });
        var themes = {};
        var themeKeys = ((_g = mergedProps().appearance) === null || _g === void 0 ? void 0 : _g.theme) &&
            //@ts-ignore
            Object.keys((_h = mergedProps().appearance) === null || _h === void 0 ? void 0 : _h.theme);
        if (themeKeys) {
            ((_j = mergedProps().appearance) === null || _j === void 0 ? void 0 : _j.theme) &&
                //@ts-ignore
                Object.values((_k = mergedProps().appearance) === null || _k === void 0 ? void 0 : _k.theme).map(function (theme, i) {
                    var _a, _b, _c, _d, _e, _f, _g, _h;
                    var key = themeKeys[i];
                    // ignore default theme
                    if (key === 'default')
                        return {};
                    var merged = merge((_d = (mergedProps().appearance &&
                        ((_a = mergedProps().appearance) === null || _a === void 0 ? void 0 : _a.theme) &&
                        ((_c = (_b = mergedProps().appearance) === null || _b === void 0 ? void 0 : _b.theme) === null || _c === void 0 ? void 0 : _c[key]))) !== null && _d !== void 0 ? _d : {}, (_h = (mergedProps().appearance &&
                        ((_e = mergedProps().appearance) === null || _e === void 0 ? void 0 : _e.variables) &&
                        ((_g = (_f = mergedProps().appearance) === null || _f === void 0 ? void 0 : _f.variables) === null || _g === void 0 ? void 0 : _g[key]))) !== null && _h !== void 0 ? _h : {});
                    themes[themeKeys[i]] = merged;
                });
        }
        setThemes(themes);
    });
    /**
     * Wraps around all auth components
     * renders the social auth providers if SignView is true
     *
     * also handles the theme override
     *
     */
    var Container = function (props) {
        var _a, _b, _c, _d;
        return (<div class={mergedProps().theme !== 'default'
                ? createTheme(merge(
                // @ts-ignore
                (_a = mergedProps().appearance) === null || _a === void 0 ? void 0 : _a.theme[mergedProps().theme], (_d = (_c = (_b = mergedProps().appearance) === null || _b === void 0 ? void 0 : _b.variables) === null || _c === void 0 ? void 0 : _c[mergedProps().theme]) !== null && _d !== void 0 ? _d : {}))
                : ''}>
      <Show when={SignView()}>
        <SocialAuth appearance={mergedProps().appearance} supabaseClient={mergedProps().supabaseClient} providers={mergedProps().providers} providerScopes={mergedProps().providerScopes} queryParams={mergedProps().queryParams} socialLayout={mergedProps().socialLayout} redirectTo={mergedProps().redirectTo} onlyThirdPartyProviders={mergedProps().onlyThirdPartyProviders} i18n={i18n()} view={authView()}/>
      </Show>

      <Show when={!mergedProps().onlyThirdPartyProviders}>
        {props.children}
      </Show>
    </div>);
    };
    createEffect(function () {
        /**
         * Overrides the authview if it is changed externally
         */
        var authListener = mergedProps().supabaseClient.auth.onAuthStateChange(function (event, session) {
            if (event === 'PASSWORD_RECOVERY') {
                setAuthView(VIEWS.UPDATE_PASSWORD);
            }
            else if (event === 'USER_UPDATED') {
                setAuthView(VIEWS.SIGN_IN);
            }
        }).data;
        setAuthView(mergedProps().view);
        onCleanup(function () { return authListener.subscription.unsubscribe(); });
    });
    var _f = createStore({
        supabaseClient: mergedProps().supabaseClient,
        setAuthView: setAuthView,
        defaultEmail: defaultEmail(),
        defaultPassword: defaultPassword(),
        setDefaultEmail: setDefaultEmail,
        setDefaultPassword: setDefaultPassword,
        redirectTo: mergedProps().redirectTo,
        magicLink: mergedProps().magicLink,
        showLinks: mergedProps().showLinks,
        i18n: i18n()
    }), emailProp = _f[0], setEmailProp = _f[1];
    /**
     * View handler, displays the correct Auth view
     * all views are wrapped in <Container/>
     */
    return (<Switch fallback={null}>
      <Match when={authView() === VIEWS.SIGN_IN}>
        <Container>
          <EmailAuth {...emailProp} authView={'sign_in'} appearance={mergedProps().appearance}/>
        </Container>
      </Match>

      <Match when={authView() === VIEWS.SIGN_UP}>
        <Container>
          <EmailAuth appearance={mergedProps().appearance} supabaseClient={mergedProps().supabaseClient} authView={'sign_up'} setAuthView={setAuthView} defaultEmail={defaultEmail()} defaultPassword={defaultPassword()} setDefaultEmail={setDefaultEmail} setDefaultPassword={setDefaultPassword} redirectTo={mergedProps().redirectTo} magicLink={mergedProps().magicLink} showLinks={mergedProps().showLinks} i18n={i18n()} additionalData={mergedProps().additionalData}>
            {props.children}
          </EmailAuth>
        </Container>
      </Match>

      <Match when={authView() === VIEWS.FORGOTTEN_PASSWORD}>
        <ForgottenPassword appearance={mergedProps().appearance} supabaseClient={mergedProps().supabaseClient} setAuthView={setAuthView} redirectTo={mergedProps().redirectTo} showLinks={mergedProps().showLinks} i18n={i18n()}/>
      </Match>

      <Match when={authView() === VIEWS.MAGIC_LINK}>
        <Container>
          <MagicLink appearance={mergedProps().appearance} supabaseClient={mergedProps().supabaseClient} setAuthView={setAuthView} redirectTo={mergedProps().redirectTo} showLinks={mergedProps().showLinks} i18n={i18n()}/>
        </Container>
      </Match>

      <Match when={authView() === VIEWS.UPDATE_PASSWORD}>
        <UpdatePassword appearance={mergedProps().appearance} supabaseClient={mergedProps().supabaseClient} i18n={i18n()}/>
      </Match>

      <Match when={authView() === VIEWS.VERIFY_OTP}>
        <VerifyOtp appearance={mergedProps().appearance} supabaseClient={mergedProps().supabaseClient} setAuthView={setAuthView} otpType={mergedProps().otpType} i18n={i18n()}/>
      </Match>
    </Switch>);
}
Auth.ForgottenPassword = ForgottenPassword;
Auth.UpdatePassword = UpdatePassword;
Auth.MagicLink = MagicLink;
Auth.UserContextProvider = UserContextProvider;
Auth.useUser = useUser;
export default Auth;
